import { DatePipe } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  SecurityContext,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import {
  ModalController,
  NavController,
  ToastController,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs/operators';
import { PinSelectModalComponent } from 'src/app/_components/pin-select-modal/pin-select-modal.component';
import { PlanMarkerCreateComponent } from 'src/app/_components/plan-marker-create/plan-marker-create.component';
import { ExtFile } from 'src/app/_model/ext-file';
import { IMSId, IMSObjectType } from 'src/app/_model/ims-object';
import { Pin } from 'src/app/_model/pin';
import { PinType, PinTypeAnchor } from 'src/app/_model/pin-type';
import { ActionInterpreterService } from 'src/app/_services/action-interpreter/action-interpreter.service';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { DynFormsService } from 'src/app/_services/dyn-forms/logic/dyn-forms.service';
import { EmployeeService } from 'src/app/_services/employee/logic/employee.service';
import { ExtFilesService } from 'src/app/_services/ext-files/logic/ext-files.service';
import { FileManagerService } from 'src/app/_services/file-manager/file-manager.service';
import { LoadingService } from 'src/app/_services/loading/loading.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { PinTypesService } from 'src/app/_services/pin-types/logic/pin-types.service';
import { PinsService } from 'src/app/_services/pins/logic/pins.service';
import { IMSComponent } from 'src/app/_util/ims-component';
import { NavigationOptions } from '@ionic/angular/common/providers/nav-controller';
import { DynForm } from 'src/app/_model/dyn-form';
import { DeficiencyTicketsService } from 'src/app/_services/deficiency-ticket/logic/deficiency-tickets.service';
import { MenuService } from 'src/app/_services/menu/logic/menu.service';
import * as pdfjsLib from 'pdfjs-dist';
import canvasSize from 'canvas-size';
import { IonModal } from '@ionic/angular/common';
import { DeficiencyTicket } from 'src/app/_model/deficiency-ticket';
import {
  PhotoViewAction,
  PhotoViewModalComponent,
} from 'src/app/_components/photo-view-modal/photo-view-modal.component';
import { PhotoGalleryImage } from 'src/app/_components/photo-gallery/photo-gallery.component';
import { LBSErrorHandlerService } from 'src/app/_services/error-handler/error-handler.service';
import { ErrorService } from 'src/app/_services/error/error.service';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import {
  getStatusIconForExtFile,
  getStatusIconLibraryForExtFile,
  isDownloaded,
} from 'src/app/_util/lbs-extfile-utils';

const DEFAULT_SCALE_DELTA = 1.2;
// currentScale is inverse: rendered width = fit width / currentScale.
// A value > 1 allows zooming out below the fit-to-width size.
const MAX_SCALE = 3.0;
// PDF points are 1/72 inch, CSS pixels 1/96 inch
const PDF_TO_CSS_UNITS = 96 / 72;
// Upper bound for the initial fit on wide screens (relative to the 100% size),
// otherwise a portrait page would be stretched over the full desktop width
const MAX_FIT_ZOOM = 1.5;
const FALLBACK_FIT_WIDTH = 358;

type ScrollItem = {
  type: 'PHOTOHEADER' | 'DTHEADER' | 'NOTEHEADER' | 'PHOTO' | 'DT' | 'NOTE';
  extFile?: ExtFile;
  deficiencyTicket?: DeficiencyTicket;
  index?: number;
};

@Component({
  selector: 'app-document-viewer',
  templateUrl: './document-viewer.page.html',
  styleUrls: ['./document-viewer.page.scss'],
})
export class DocumentViewerPage
  extends IMSComponent
  implements OnInit, OnDestroy
{
  @ViewChild('thecanvas', { static: true }) canvas: ElementRef;
  @ViewChild('wrapper', { static: true }) wrapper: ElementRef;
  @ViewChild('container', { static: true }) container: ElementRef;
  @Input('extFileId')
  set extFileId(id) {
    this.logger.log(
      this.constructor.name,
      'Received extFileId as Input (Modal)'
    );
    this.isRequestedAsModal = true;
    this.extFile = this.extFilesService.getExtFileById({
      imsId: id,
      secondaryId: undefined,
      objRef: 'ims/ExtFile',
    });
    if (this.extFile) {
      this.logger.log(
        this.constructor.name,
        'Loaded ExtFile',
        this.extFile.label
      );

      this.loadFile();
    }
  }

  public get pageTitle(): string {
    if (this.extFile) {
      return this.extFile.fileidentifier;
    } else if (this.temporaryDisplayName) {
      return this.temporaryDisplayName;
    } else {
      return this.translate.instant('PAGE.PLANMARKER.TITLE.HEADER');
    }
  }
  public get curPageNum(): number {
    return this.currentPage;
  }

  public get pageCount(): number {
    return this.pdf?.numPages ?? 0;
  }

  public get disablePreviousPage(): boolean {
    return this.pdf == undefined || this.currentPage <= 1;
  }

  public get disableNextPage(): boolean {
    return this.pdf == undefined || this.currentPage >= this.pageCount;
  }

  public get disableZoomOut(): boolean {
    return this.currentScale >= MAX_SCALE;
  }

  public get disableZoomIn(): boolean {
    return this.currentScale <= this.minScale;
  }

  // General
  public backLabel: string;
  public isRequestedAsModal: boolean = false;
  private temporaryFileName: string;
  private temporaryDisplayName: string;

  // PDF.js
  public src: Uint8Array;
  public extFile: ExtFile;
  public isRendering = false;

  private pdf: pdfjsLib.PDFDocumentProxy;
  private page: pdfjsLib.PDFPageProxy;
  private currentViewport: pdfjsLib.PageViewport;
  private renderTask: pdfjsLib.RenderTask;
  private currentScale: number = 1;
  private currentPage: number = 1;
  private minScale: number;
  private resizeTimer: ReturnType<typeof setTimeout>;
  // Incremented per loadPage, so a stale sharpen pass does not overwrite a newer page
  private renderGeneration = 0;

  private get pdfWrapper(): HTMLDivElement {
    return this.wrapper.nativeElement as HTMLDivElement;
  }
  private get pdfCanvas(): HTMLCanvasElement {
    return this.canvas.nativeElement as HTMLCanvasElement;
  }

  // Lifecycle
  constructor(
    translate: TranslateService,
    logger: LoggingService,
    private route: ActivatedRoute,
    private appParameterService: AppParameterService,
    private extFilesService: ExtFilesService,
    private fileManager: FileManagerService,
    private pinsService: PinsService,
    private pinTypesService: PinTypesService,
    private loadingService: LoadingService,
    private employeeService: EmployeeService,
    private modalController: ModalController,
    private toastController: ToastController,
    private navigationController: NavController,
    private datePipe: DatePipe,
    private zone: NgZone,
    private actionInterpreter: ActionInterpreterService,
    private renderer: Renderer2,
    private navController: NavController,
    private sanitizer: DomSanitizer,
    private dynFormsService: DynFormsService,
    private deficiencyTicketService: DeficiencyTicketsService,
    private menuService: MenuService,
    private errorHandlerService: LBSErrorHandlerService,
    private errorService: ErrorService,
    private clientStateService: ClientStateService
  ) {
    super(logger, translate);
    pdfjsLib.GlobalWorkerOptions.workerSrc = './assets/pdfjs/pdf.worker.mjs';
  }

  async ngOnInit() {
    super.ngOnInit();

    Device.getInfo().then((info) => {
      if (info.operatingSystem === 'ios') {
        this.backLabel = this.translate.instant('COMMON.BACK');
      } else {
        this.backLabel = undefined;
      }
    });

    super.manageSubscription(
      this.route.paramMap.subscribe({
        next: async (params) => {
          const id = params.get('id');
          const filename = params.get('filename');
          const displayname = params.get('displayname');

          if (id) {
            this.logger.log(
              this.constructor.name,
              'Received extFileId as Request Param'
            );
            this.extFile = this.extFilesService.getExtFileById({
              imsId: +id,
              secondaryId: undefined,
              objRef: 'ims/ExtFile',
            });
            if (this.extFile) {
              this.logger.log(
                this.constructor.name,
                'Loaded ExtFile',
                this.extFile.label
              );

              this.loadFile();
            } else {
              this.logger.error(
                this.constructor.name,
                'Could not display extfile because extfile is null',
                id
              );
            }
          } else if (filename) {
            this.logger.log(
              this.constructor.name,
              'Received temporary file name'
            );
            this.temporaryFileName = filename;
            this.temporaryDisplayName = displayname;
            this.loadTempFile();
          }
        },
      })
    );
  }

  // Cleanup Listeners and Subscriptions to prevent shutdowns from iOS due to ressoruces
  ngOnDestroy() {
    clearTimeout(this.resizeTimer);
    super.ngOnDestroy();
  }

  @HostListener('window:resize')
  onWindowResize() {
    // The zoom level is relative to the fit width, so a re-render keeps it
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (!this.page) {
        return;
      }
      if (this.renderTask) {
        this.renderTask.cancel();
      } else {
        this.loadPage();
      }
    }, 200);
  }

  // Public Functions

  public cancel() {
    this.modalController.dismiss({
      canceled: true,
    });
  }

  public pdfViewZoomOut() {
    this.currentScale = Math.min(
      MAX_SCALE,
      this.currentScale * DEFAULT_SCALE_DELTA
    );
    if (this.renderTask) {
      this.renderTask.cancel();
    } else {
      this.loadPage();
    }
  }

  public pdfViewZoomIn() {
    this.currentScale = Math.max(
      this.minScale ?? 0,
      this.currentScale / DEFAULT_SCALE_DELTA
    );
    if (this.renderTask) {
      this.renderTask.cancel();
    } else {
      this.loadPage();
    }
  }

  public async pdfViewPrevious() {
    if (this.currentPage <= 1) {
      return;
    }
    this.currentPage--;
    this.page = await this.pdf.getPage(this.currentPage);
    if (this.renderTask) {
      this.renderTask.cancel();
    } else {
      this.loadPage();
    }
  }

  public async pdfViewNext() {
    if (this.currentPage >= this.pdf.numPages) {
      return;
    }
    this.currentPage++;
    this.page = await this.pdf.getPage(this.currentPage);
    if (this.renderTask) {
      this.renderTask.cancel();
    } else {
      this.loadPage();
    }
  }

  // Private Functions

  private async loadFile() {
    if (this.extFile) {
      await this.loadingService.showLoading();

      const data = await this.fileManager.getExtFileBlob(
        this.extFile
      );
      var loadingTask = pdfjsLib.getDocument({ data: data });
      try {
        this.pdf = await loadingTask.promise;
        await this.loadingService.dismissLoading();
        this.logger.log(this.constructor.name, 'PDF loaded');

        // Fetch the first page
        this.currentPage = 1;
        this.currentScale = 1;
        this.page = await this.pdf.getPage(this.currentPage);
        this.loadPage();
      } catch (reason) {
        // PDF loading error
        this.logger.error(this.constructor.name, reason);
      }
    }
  }

  private async loadTempFile() {
    if (this.temporaryFileName) {
      await this.loadingService.showLoading();

      const data = await this.fileManager.getTempFileBlob(
        this.temporaryFileName
      );
      var loadingTask = pdfjsLib.getDocument({ data: data });
      try {
        this.pdf = await loadingTask.promise;
        await this.loadingService.dismissLoading();
        this.logger.log(this.constructor.name, 'PDF loaded');

        // Fetch the first page
        this.currentPage = 1;
        this.currentScale = 1;
        this.page = await this.pdf.getPage(this.currentPage);
        this.loadPage();
      } catch (reason) {
        // PDF loading error
        this.logger.error(this.constructor.name, reason);
      }
    }
  }

  private async loadPage() {
    const generation = ++this.renderGeneration;
    try {
      this.isRendering = true;

      const desiredWidth = this.getFitWidth() / this.currentScale;
      const viewport = this.page.getViewport({ scale: 1 });
      const scale = desiredWidth / viewport.width;
      this.currentViewport = this.page.getViewport({ scale: scale });

      const resetScale = async () => {
        this.currentScale = Math.min(
          MAX_SCALE,
          this.currentScale * DEFAULT_SCALE_DELTA
        );
        this.minScale = this.currentScale;
        this.logger.log(
          this.constructor.name,
          'Can Render Canvas failed - go back one zoome level and set min scale to',
          this.minScale
        );
        await this.loadPage();
      };
      try {
        this.logger.log(
          this.constructor.name,
          `try to render a canvas on scale ${this.currentScale} with size ${
            this.currentViewport.width
          }/${this.currentViewport.height}(${
            this.currentViewport.width * this.currentViewport.height
          })`
        );
        if (!(await this.canRenderCanvas(1))) {
          await resetScale();
          return;
        }
      } catch (e) {
        this.logger.error(this.constructor.name, 'Can Render Canvas error:', e);
        await resetScale();
        return;
      }

      this.logger.log(
        this.constructor.name,
        'Render page',
        this.currentPage,
        'with scale',
        this.currentScale
      );
      // Prepare canvas using PDF page dimensions
      this.pdfCanvas.width = Math.floor(this.currentViewport.width);
      this.pdfCanvas.height = Math.floor(this.currentViewport.height);
      this.pdfCanvas.style.width =
        Math.floor(this.currentViewport.width) + 'px';
      this.pdfCanvas.style.height =
        Math.floor(this.currentViewport.height) + 'px';

      this.pdfWrapper.style.width =
        Math.floor(this.currentViewport.width) + 'px';
      this.pdfWrapper.style.height =
        Math.floor(this.currentViewport.height) + 'px';

      // First pass in CSS resolution: cheap, so the page shows up quickly
      await this.renderInto(this.pdfCanvas, 1);
      this.logger.log(
        this.constructor.name,
        'Page',
        this.currentPage,
        'rendered'
      );
      this.isRendering = false;

      // Second pass in device resolution, swapped in once done
      await this.sharpenPage(generation);
    } catch (e) {
      this.isRendering = false;
      this.renderTask = undefined;
      if (e?.name === 'RenderingCancelledException') {
        this.logger.log(this.constructor.name, 'Reload Page after cancel');
        this.loadPage();
      } else {
        this.logger.error(
          this.constructor.name,
          'Failed Rendering page',
          this.currentPage,
          ' with error:',
          e
        );
      }
    }
  }

  private async renderInto(canvas: HTMLCanvasElement, outputScale: number) {
    this.renderTask = this.page.render({
      canvasContext: canvas.getContext('2d'),
      canvas: canvas,
      viewport: this.currentViewport,
      transform:
        outputScale !== 1
          ? [outputScale, 0, 0, outputScale, 0, 0]
          : undefined,
    });
    await this.renderTask.promise;
    this.renderTask = undefined;
  }

  /**
   * Re-renders the current page for HiDPI screens into an offscreen canvas
   * and copies it over the visible one, so the user never sees a blank page.
   * Skipped if the canvas would exceed the device limits.
   */
  private async sharpenPage(generation: number) {
    const outputScale = window.devicePixelRatio || 1;
    if (
      outputScale <= 1 ||
      !(await this.canRenderCanvas(outputScale)) ||
      generation !== this.renderGeneration
    ) {
      return;
    }
    const hiDpiCanvas = document.createElement('canvas');
    try {
      hiDpiCanvas.width = Math.floor(this.currentViewport.width * outputScale);
      hiDpiCanvas.height = Math.floor(
        this.currentViewport.height * outputScale
      );
      await this.renderInto(hiDpiCanvas, outputScale);

      this.pdfCanvas.width = hiDpiCanvas.width;
      this.pdfCanvas.height = hiDpiCanvas.height;
      this.pdfCanvas.getContext('2d').drawImage(hiDpiCanvas, 0, 0);
    } finally {
      // Release the backing store right away (iOS has a tight canvas memory limit)
      hiDpiCanvas.width = 0;
      hiDpiCanvas.height = 0;
    }
  }

  private async canRenderCanvas(outputScale: number): Promise<boolean> {
    const { success } = await canvasSize.test({
      width: Math.floor(this.currentViewport.width * outputScale),
      height: Math.floor(this.currentViewport.height * outputScale),
    });
    return success;
  }

  /** CSS width a page gets at zoom level 1 (fit to the available width). */
  private getFitWidth(): number {
    const container = this.container?.nativeElement as HTMLDivElement;
    if (!container || container.clientWidth === 0) {
      return FALLBACK_FIT_WIDTH;
    }
    const style = getComputedStyle(container);
    const available =
      container.clientWidth -
      parseFloat(style.paddingLeft) -
      parseFloat(style.paddingRight);
    const nativeWidth =
      this.page.getViewport({ scale: PDF_TO_CSS_UNITS }).width * MAX_FIT_ZOOM;
    return Math.max(1, Math.floor(Math.min(available, nativeWidth)));
  }
}
